import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CheckCircle2, LockKeyhole, PlayCircle } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchCourseItems, fetchCourseProgress, fetchVolumeBySlug, fetchVolumeStructure, isVolumeEntitled } from '../../lib/hub-api';
import type { CourseItem, CourseModule, CourseProgress, Stage, Volume } from '../../types/hub';

export function VolumeOverviewPage() {
  const { volumeSlug = '' } = useParams();
  const { user } = useAuth();
  const [volume, setVolume] = useState<Volume | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [items, setItems] = useState<CourseItem[]>([]);
  const [progress, setProgress] = useState<CourseProgress[]>([]);
  const [owned, setOwned] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const nextVolume = await fetchVolumeBySlug(volumeSlug);
        if (!nextVolume) throw new Error('This rebuild could not be found.');
        const [entitled, structure] = await Promise.all([isVolumeEntitled(nextVolume.id), fetchVolumeStructure(nextVolume.id)]);
        const nextItems = entitled ? await fetchCourseItems(nextVolume.id) : [];
        const nextProgress = entitled && user ? await fetchCourseProgress(user.id, nextItems.map((item) => item.id)) : [];
        if (active) {
          setVolume(nextVolume); setOwned(entitled); setStages(structure.stages); setModules(structure.modules);
          setItems(nextItems); setProgress(nextProgress);
        }
      } catch (cause) { if (active) setError(cause instanceof Error ? cause.message : 'Unable to open this rebuild.'); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [user, volumeSlug]);

  const completedIds = useMemo(() => new Set(progress.filter((entry) => entry.completed_at).map((entry) => entry.course_item_id)), [progress]);
  const completedCount = items.filter((item) => completedIds.has(item.id)).length;
  const percentage = items.length ? Math.round((completedCount / items.length) * 100) : 0;
  const lastItem = useMemo(() => {
    const lastProgress = [...progress].sort((a, b) => new Date(b.last_viewed_at).getTime() - new Date(a.last_viewed_at).getTime())[0];
    return items.find((item) => item.id === lastProgress?.course_item_id) ?? items[0] ?? null;
  }, [items, progress]);
  const lastModule = lastItem ? modules.find((module) => module.id === lastItem.module_id) : null;
  const courseHref = lastItem && lastModule
    ? `/hub/rebuilds/${volumeSlug}/course/${lastModule.slug}/${lastItem.slug}`
    : `/hub/rebuilds/${volumeSlug}/course`;

  if (loading) return <div className="page-loading"><span className="loader" />Checking access…</div>;
  if (error || !volume) return <div className="hub-page"><div className="error-banner">{error || 'Rebuild not found.'}</div></div>;

  const publicSite = import.meta.env.VITE_PUBLIC_SITE_URL || 'https://therebuildprocess.com';
  const salesHref = volume.sales_path ? `${publicSite}${volume.sales_path}` : null;

  return (
    <div className="hub-page">
      <header className="volume-overview-header" style={{ borderColor: volume.accent_color || '#0F4C81' }}>
        <div><p className="eyebrow">Volume {volume.roman_numeral}</p><h1>{volume.short_title}</h1><h2>{volume.subtitle}</h2><p>{volume.description}</p></div>
        <span className={`status-chip ${owned ? 'status-owned' : 'status-locked'}`}>{owned ? 'Unlocked' : 'Locked preview'}</span>
      </header>

      {!owned ? (
        <section className="locked-preview card">
          <div className="lock-emblem"><LockKeyhole /></div>
          <p className="eyebrow">Volume-specific access</p><h2>Unlock the complete {volume.short_title}</h2>
          <p>Your purchase opens this volume’s course, stories, lessons, ebook, audiobook, workbook tools, anchors, Blueprint, Rebuild Map, Delphi context, coaching, and library inside this same account.</p>
          {stages.length > 0 && <div className="preview-stage-list">{stages.map((stage) => <span key={stage.id}>{stage.title}</span>)}</div>}
          {salesHref ? <a className="button clay" href={salesHref}>Explore the {volume.short_title} <ArrowRight size={17} /></a> : <button className="button secondary" disabled>Coming soon</button>}
        </section>
      ) : (
        <>
          <section className="charcoal-card volume-start-card">
            <div><p className="eyebrow light">Your {volume.short_title}</p><h2>Learn it. Build it. Live it.</h2><p>The Story and lesson player is live. Watch, listen, read, save notes, mark progress, and return exactly where you left off.</p>
              <div className="volume-progress-inline"><div><span style={{ width: `${percentage}%` }} /></div><small>{percentage}% complete · {completedCount} of {items.length} course items</small></div>
            </div>
            {modules.length ? <Link className="button clay" to={courseHref}><PlayCircle size={17} />{progress.length ? 'Continue the course' : 'Start the course'}</Link> : null}
          </section>
          <section className="stage-list">
            {stages.map((stage) => (
              <article className="card stage-card" key={stage.id}>
                <p className="eyebrow">Stage {stage.sort_order}</p><h2>{stage.title}</h2><p>{stage.description}</p>
                <div className="module-list">
                  {modules.filter((module) => module.stage_id === stage.id).map((module, moduleIndex) => {
                    const moduleItems = items.filter((item) => item.module_id === module.id);
                    const moduleComplete = moduleItems.length > 0 && moduleItems.every((item) => completedIds.has(item.id));
                    const first = moduleItems[0];
                    return (
                      <Link className="module-row module-row-link" key={module.id} to={first ? `/hub/rebuilds/${volumeSlug}/course/${module.slug}/${first.slug}` : courseHref}>
                        <span>{modules.indexOf(module) + 1}</span><div><b>{module.title}</b><p>{module.description}</p><small>{moduleItems.length} items · {module.anchor_title || 'Anchor pending'}</small></div>{moduleComplete ? <CheckCircle2 className="module-complete-icon" /> : <ArrowRight size={18} />}
                      </Link>
                    );
                  })}
                </div>
              </article>
            ))}
          </section>
        </>
      )}
    </div>
  );
}
